import { useEffect } from 'react';
import { Editor } from '@tiptap/react';
import { useTiptapEditorDispatch } from '../../../components/tiptapEditor/store/tiptapEditorStoreHooks';
import { setActiveTiptapEditor } from '../../../components/tiptapEditor/store/tiptapEditorStoreActions';

type UseCommentFocusHandlingProps = {
    editor: Editor | null;
    isUpdating: boolean;
    isEditing: boolean;
    startEditing: () => void;
    onBlur?: () => void;
};

/**
 * Custom hook for handling focus behavior in comment editors.
 * Manages auto-focus on update and blur event handling with different behaviors
 * for existing comment edits vs reply forms.
 */
export const useCommentFocusHandling = (props: UseCommentFocusHandlingProps) => {
    const { editor, isUpdating, isEditing, startEditing, onBlur } = props;

    const tiptapDispatch = useTiptapEditorDispatch();

    /**
     * Determines whether the editor should auto-focus when entering update mode.
     * Auto-focus behavior varies based on editor type and content state:
     * - For existing comment edits (onBlur exists): Always auto-focus
     * - For reply forms (onBlur is undefined): Only auto-focus when no content exists to prevent bounce
     */
    const shouldAutoFocusEditor = (hasContent: boolean): boolean => {
        // For existing comment edits, always auto-focus regardless of content
        const shouldStopEditingOnBlur = onBlur !== undefined;
        if (shouldStopEditingOnBlur) return true;

        // For reply forms, only auto-focus when there's no content to prevent bounce behavior
        return !hasContent;
    };

    /**
     * Determines whether the editor should focus and start editing based on current state.
     * Separates the decision logic from the execution logic for cleaner architecture.
     */
    const getShouldFocusAndStartEditing = (isEditing: boolean, editor: Editor): boolean => {
        if (isEditing) {
            // If already editing, just set editable and update toolbar, but don't auto-focus
            // This prevents the bounce behavior when user clicks outside
            return false;
        }

        const editorText = editor.getText().trim();
        const hasContent = editorText.length > 0;

        if (shouldAutoFocusEditor(hasContent)) {
            // Auto-focus behavior with startEditing call
            return true;
        }
        // No auto-focus - just set up the editor state without focusing
        return false;
    };

    // Manual focus when comment enters updating mode
    useEffect(() => {
        if (!editor || !isUpdating) return;

        const shouldFocusAndStartEditing = getShouldFocusAndStartEditing(isEditing, editor);

        // Use a requestAnimationFrame to ensure proper initialization
        requestAnimationFrame(() => {
            // Call startEditing if requested (for new editing sessions)
            if (shouldFocusAndStartEditing) {
                startEditing();
            }

            // Always set the editor to editable
            editor.setEditable(true);

            // Focus the editor if requested
            if (shouldFocusAndStartEditing) {
                editor.commands.focus();
            }

            // Always update the toolbar state
            tiptapDispatch(setActiveTiptapEditor(editor));
        });
    }, [isUpdating, isEditing, editor, tiptapDispatch, startEditing]);

    // Handle blur events to stop editing when clicking outside
    useEffect(() => {
        if (!editor || !isUpdating) return;

        const handleBlur = () => {
            // Clear the active editor from the toolbar state when losing focus
            tiptapDispatch(setActiveTiptapEditor(null));
            // Call onBlur if provided
            onBlur?.();
        };

        editor.on('blur', handleBlur);

        return () => {
            editor.off('blur', handleBlur);
        };
    }, [editor, isUpdating, onBlur, tiptapDispatch]);
};
